import React, {useRef, useState} from 'react';
import {DragDropArea, IconCircle, FileList, LicenseRenewalContainer} from "./LicenseRenewal.styled";
import {Box, Button, Stack, TextField, Typography} from "@mui/material";
import {Trans, useTranslation} from "react-i18next";
import {theme, tokens} from "../../../../../theme";
import CloseIcon from "@mui/icons-material/Close";
import {ReactComponent as UploadIcon} from '../../../../../assets/icons/upload_ocr.svg';
import {useSnackbar} from "../../../../../contexts/SnackbarContext";


interface LicenseRenewalProps {
    isSubmitting: boolean;
    onSubmitKeyValue: (licenseKey: string) => Promise<void>;
    onSubmitFile: (file: File) => Promise<void>;
}

const LicenseRenewal = ({isSubmitting, onSubmitKeyValue, onSubmitFile}: LicenseRenewalProps) => {
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();

    const [keyValue, setKeyValue] = useState<string>('');

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [file, setFile] = useState<File | null>(null);

    const isTxtFile = (file: File) =>
        file.name.toLowerCase().endsWith('.txt') || file.type === 'text/plain';

    const verifyInput = (files: File[]): File | null => {
        if (files.length > 1) {
            if (fileInputRef.current)
                fileInputRef.current.value = '';

            showSnackbar(t('licenseManager.renewal.tooManyFilesError'), 'error');
            return null;
        }

        const file = files[0];

        if (!isTxtFile(file)) {
            if (fileInputRef.current)
                fileInputRef.current.value = '';

            showSnackbar(t('licenseManager.renewal.wrongFileFormatError'), 'error');
            return null;
        }

        return file;
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();

        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        const file = verifyInput(files);

        if (file) setFile(file);
    };

    const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClearInputs = () => {
        setFile(null);
        setKeyValue('');
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            const files = Array.from(e.target.files);
            const file = verifyInput(files);
            e.target.value = '';

            if (file) setFile(file);
        }
    };

    const handleSubmitInput = async () => {
        if (!file && keyValue.trim().length > 0) {
            await onSubmitKeyValue(keyValue);
        }

        if (keyValue.length === 0 && !!file) {
            await onSubmitFile(file);
        }

        setFile(null);
        setKeyValue('');
    };

    return (
        <LicenseRenewalContainer role="region" aria-labelledby="license-renewal-container-title">
            <Typography variant="h5" component="div" id="license-renewal-container-title">
                {t('licenseManager.renewal.title')}
            </Typography>

            <Typography variant="body1" component="p">
                <Trans
                    i18nKey="licenseManager.renewal.subtitle"
                    components={{ bold: <strong /> }}
                />
            </Typography>

            <Stack direction="column" spacing={2} mt={2}>
                <Typography variant="caption" component="p" textTransform="uppercase" fontWeight="medium">
                    {t('licenseManager.renewal.licenseFileLabel')}
                </Typography>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,text/plain"
                    style={{display: 'none'}}
                    onChange={handleInputChange}
                    aria-hidden="true"
                    tabIndex={-1}
                />

                <DragDropArea
                    disableRipple
                    disabled={keyValue.trim().length > 0 || isSubmitting}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                    }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={onDrop}
                    $isDragging={isDragging}
                    aria-label={t('licenseManager.renewal.dragDropArea')}
                >
                    <IconCircle>
                        <UploadIcon aria-hidden="true" />
                    </IconCircle>

                    <Typography variant="body1" fontWeight={tokens.fontWeight.medium} sx={{ color: theme.palette.text.primary }}>
                        {t('licenseManager.renewal.dragDropArea')}
                    </Typography>

                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {t('licenseManager.renewal.formatsInfo')}
                    </Typography>

                    {!!file && (
                        <Box flexDirection="column" display="flex" gap={2} pl={3} pr={3}>
                            <Typography variant="body2" color="text.hint" mt={3}>
                                {t('licenseManager.renewal.selectedFiles')}
                            </Typography>

                            <FileList>
                                <li>
                                    <span>{file.name}</span>
                                    <button type="button" onClick={handleRemoveFile} aria-label={t('common.remove') as string}>
                                        <CloseIcon sx={{ fontSize: '16px', marginLeft: '8px' }} />
                                    </button>
                                </li>
                            </FileList>
                        </Box>
                    )}
                </DragDropArea>
            </Stack>

            <Stack direction="column" spacing={2} mt={3} mb={3}>
                <Typography variant="caption" component="p" textTransform="uppercase" fontWeight="medium">
                    {t('licenseManager.renewal.licenseKeyLabel')}
                </Typography>

                <TextField
                    value={keyValue}
                    onChange={(e) => setKeyValue(e.target.value)}
                    variant="outlined"
                    placeholder={t('licenseManager.renewal.keyPlaceholder') as string ?? ''}
                    multiline
                    minRows={5}
                    fullWidth
                    disabled={!!file || isSubmitting}
                />
            </Stack>

            <Stack direction="row" spacing={2} sx={{ alignSelf: 'flex-end' }}>
                <Button
                    variant="text"
                    type="button"
                    onClick={handleClearInputs}
                    disabled={isDragging || isSubmitting || !(file || keyValue.trim().length > 0)}
                >
                    {t('common.clear')}
                </Button>

                <Button
                    variant="contained"
                    type="button"
                    onClick={handleSubmitInput}
                    disabled={isDragging || isSubmitting || !(file || keyValue.trim().length > 0)}
                >
                    {t('licenseManager.renewal.renewLicenseBtn')}
                </Button>
            </Stack>
        </LicenseRenewalContainer>
    );
};

export default LicenseRenewal;